import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  GetTodayBadgeCounts, MarkAllMissedCallsRead, MissedCallMarkedRead,
  UpdateCounts,
  UserActionItemCompleted
} from 'ng2-app/modules/today/state/today-badge-counts/today-badge-counts.action';
import {
  RequestCounts,
  RequestTypeCount,
  TodayBadgeCountsService
} from 'ng2-app/modules/today/services/today-badge-counts.service';
import { tap } from 'rxjs';
import { CallHistoryService } from 'ng2-app/services/call/call-history.service';
import { patch, updateItem } from '@ngxs/store/operators';


export class TodayBadgeCountsStateModel {
  missedCalls?: number;
  unreadEmailThreads?: number;
  unknownUnreadEmailThreads?: number;
  unreadSmsThreads?: number;
  unreadUnknownSmsThreads?: number;
  requests?: RequestCounts;
  today?: number;
  total?: number;
}

@State<TodayBadgeCountsStateModel>({
  name: 'TodayBadgeCounts',
  defaults: {
    missedCalls: 0,
    unreadEmailThreads: 0,
    unknownUnreadEmailThreads: 0,
    unreadSmsThreads: 0,
    unreadUnknownSmsThreads: 0,
    requests: {
      requestCountTotal: 0,
      byType: [],
    },
    today: 0,
    total: 0
  }
})
@Injectable()
export class TodayBadgeCountsState {

  @Selector()
  static missedCalls( state: TodayBadgeCountsStateModel ) {
    return state.missedCalls;
  }

  @Selector()
  static unreadEmailThreads( state: TodayBadgeCountsStateModel ) {
    return state.unreadEmailThreads;
  }

  @Selector()
  static unknownUnreadEmailThreads( state: TodayBadgeCountsStateModel ) {
    return state.unknownUnreadEmailThreads;
  }

  @Selector()
  static unreadSmsThreads( state: TodayBadgeCountsStateModel ) {
    return state.unreadSmsThreads;
  }

  @Selector()
  static unreadUnknownSmsThreads( state: TodayBadgeCountsStateModel ) {
    return state.unreadUnknownSmsThreads;
  }

  @Selector()
  static total( state: TodayBadgeCountsStateModel ) {
    return state.total;
  }

  @Selector()
  static today( state: TodayBadgeCountsStateModel ) {
    return state.today;
  }

  @Selector()
  static requests( state: TodayBadgeCountsStateModel ) {
    return state.requests;
  }

  constructor(
    private todayBadgeCountsService: TodayBadgeCountsService,
    private callHistoryService: CallHistoryService,
  ) {}

  @Action( GetTodayBadgeCounts )
  getTodayBadgeCounts(
    { patchState }: StateContext<TodayBadgeCountsStateModel>,
    { lenderUserId }: GetTodayBadgeCounts
  ) {
    return this.todayBadgeCountsService.getTodayBadgeCounts( lenderUserId )
      .pipe(
        tap( (response) => {
          patchState(response);
        })
      );
  }

  @Action( UpdateCounts )
  updateCounts(
    { patchState, getState }: StateContext<TodayBadgeCountsStateModel>,
    { counts }: UpdateCounts,
  ) {
    patchState(counts);
    const state = getState();
    patchState({total: this.getTotalCount(state)});
  }

  @Action( UserActionItemCompleted )
  userActionItemCompleted(
    { patchState, getState, setState }: StateContext<TodayBadgeCountsStateModel>,
    { type, taskTypeId }: UserActionItemCompleted,
  ) {
    const state = getState();
    const todayCount = state.today ?? 0;
    const requestCount = state.requests?.requestCountTotal ?? 0;
    const total = state.total ?? 0;
    switch (type) {
      case 'today': {
        if (todayCount > 0) {
          patchState({today: todayCount - 1, total: total - 1});
        }
      }
      case 'requests': {
        if (requestCount > 0) {
          const taskTypeCount = state.requests?.byType.filter( ( taskType: RequestTypeCount ) => taskType.typeId === taskTypeId )[0];
          setState(patch({
            requests: patch({
              requestCountTotal: requestCount - 1,
              byType: updateItem( ( taskType: RequestTypeCount ) => taskType.typeId === taskTypeId, patch({
                count: taskTypeCount? taskTypeCount.count - 1 : 0
              }))
            }),
            total: total - 1
          }));
        }
      }
    }
  }

  @Action( MissedCallMarkedRead )
  missedCallMarkRead(
    { patchState, getState }: StateContext<TodayBadgeCountsStateModel>,
    { callId }: MissedCallMarkedRead
  ) {

    const state = getState();
    const total = Math.max((state.total ?? 0) - 1, 0),
      missedCalls = Math.max((state.missedCalls ?? 0) - 1, 0);

    return this.callHistoryService.markCallRead(callId).pipe(
      tap(() => {
        patchState({missedCalls, total});
      })
    );
  }

  @Action(MarkAllMissedCallsRead)
  markAllMissedCallsRead(
    { patchState }: StateContext<TodayBadgeCountsStateModel>,
    { lenderUserId }: MarkAllMissedCallsRead
  ) {
    return this.callHistoryService.markAllMissedCallsRead(lenderUserId).pipe(
      tap(() => {
        patchState({missedCalls: 0});
      })
    );
  }

  private getTotalCount( state: TodayBadgeCountsStateModel) {
    const missedCallCount = state.missedCalls ?? 0;
    const emailCount = state.unreadEmailThreads ?? 0;
    const smsCount = state.unreadSmsThreads ?? 0;
    const todayCount = state.today ?? 0;
    const requestCount = state.requests?.requestCountTotal ?? 0;
    return emailCount + smsCount + todayCount + requestCount + missedCallCount;
  }
}
